import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BusinessUnitInterface, PieceCategoryInterface } from '../../interfaces'
import { LocationDto } from '../../interfaces/Dto/LocationDto'
import { BUByLocationDto } from '../../interfaces/Location/LocationByBu'
import {
  getStoresDjango,
  getLocationsWithStoreDjango,
  getPieceCategoriesDjango,
} from '../../services'
import { getCountriesPhoneCodes } from '../../services/GeneralServices'
import { CountryPhoneOption } from '../../interfaces/General/CountryPhoneDto'
import { alertService } from '../../services/alertService'
import { mergeBulkCategories } from '../../utils/mappers'
import { formatSentence } from '../../utils/format'
import type { AppDispatch, RootState } from '../store'

// Catalogos de carga masiva servidos por la API Django (REACT_APP_URL_PATH_API_DJANGO).
// Rama Redux propia, hidratada UNA vez por sesion (lazy, al abrir carga masiva). NO se
// persiste en localStorage (blacklist en store.tsx): debe re-hidratarse fresco cada sesion
// para no servir tiendas/categorias obsoletas en la plantilla Excel.
//
//   hydrateBulkCatalogs()
//        │  loading=true, error=false
//        ▼
//   Promise.all([getStoresDjango, getLocationsWithStoreDjango])  ── falla ─▶ error=true (A1: bloquea UI + alertService.error)
//        │ ok
//        ▼
//   allSettled([categorias type=1, type=2]) ── un tipo falla ─▶ warn + usa lo disponible (A3)
//        ▼
//   loaded=true (businessUnits, cities, pieceCategories)

interface BulkCatalogsState {
  businessUnits: BusinessUnitInterface[]
  cities: LocationDto[]
  // Relacion ciudad→tienda (default_destination_store de GET /api/location). Resuelve el
  // buCode de la tienda DESTINO segun la ciudad elegida (el origen sigue saliendo de
  // user.client).
  citiesByBu: BUByLocationDto[]
  pieceCategories: PieceCategoryInterface[]
  /**
   * Países con código telefónico, para el campo "País" del teléfono del
   * destinatario. Va en el bloque TOLERANTE de la hidratación: que el catálogo
   * de países falle no puede dejar sin carga masiva a un operador que solo
   * manda guías dentro de Venezuela.
   */
  countryPhoneCodes: CountryPhoneOption[]
  /** El catálogo de países no cargó; la fila muestra el aviso y solo ofrece +58. */
  countryPhoneCodesError: boolean
  loading: boolean
  error: boolean
  loaded: boolean
}

const initialState: BulkCatalogsState = {
  businessUnits: [],
  cities: [],
  citiesByBu: [],
  pieceCategories: [],
  countryPhoneCodes: [],
  countryPhoneCodesError: false,
  loading: false,
  error: false,
  loaded: false,
}

interface BulkCatalogsPayload {
  businessUnits: BusinessUnitInterface[]
  cities: LocationDto[]
  citiesByBu: BUByLocationDto[]
  pieceCategories: PieceCategoryInterface[]
  countryPhoneCodes: CountryPhoneOption[]
  countryPhoneCodesError: boolean
}

export const bulkCatalogsSlice = createSlice({
  name: 'bulkCatalogs',
  initialState,
  reducers: {
    bulkCatalogsLoading: (state) => {
      state.loading = true
      state.error = false
    },
    bulkCatalogsLoaded: (
      state,
      action: PayloadAction<BulkCatalogsPayload>
    ) => {
      state.businessUnits = action.payload.businessUnits
      state.cities = action.payload.cities
      state.citiesByBu = action.payload.citiesByBu
      state.pieceCategories = action.payload.pieceCategories
      state.countryPhoneCodes = action.payload.countryPhoneCodes
      state.countryPhoneCodesError = action.payload.countryPhoneCodesError
      state.loading = false
      state.error = false
      state.loaded = true
    },
    bulkCatalogsError: (state) => {
      state.loading = false
      state.error = true
      state.loaded = false
    },
    resetBulkCatalogs: () => initialState,
  },
})

export const {
  bulkCatalogsLoading,
  bulkCatalogsLoaded,
  bulkCatalogsError,
  resetBulkCatalogs,
} = bulkCatalogsSlice.actions

export default bulkCatalogsSlice.reducer

// Thunk: hidrata los catalogos desde Django. Idempotente por sesion (no re-fetch salvo
// force). A1: si tiendas o ciudades fallan -> error=true + alertService.error. A3: las
// categorias van por allSettled; si un tipo falla se usa lo disponible + alertService.warn.
export const hydrateBulkCatalogs =
  (force = false) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const current = getState().bulkCatalogs
    if (!force && (current.loaded || current.loading)) return

    dispatch(bulkCatalogsLoading())
    try {
      // Un solo GET /api/location sirve las dos vistas: el listado de ciudades y la
      // relacion ciudad→tienda destino (antes GET /api/business-unit-location, eliminado).
      const [businessUnitsRaw, locations] = await Promise.all([
        getStoresDjango(),
        getLocationsWithStoreDjango(3),
      ])
      const { cities: citiesRaw, citiesByBu } = locations

      const cities = [...citiesRaw]
        .map((c) => ({ ...c, locationName: formatSentence(c.locationName) }))
        .sort((a, b) =>
          a.locationName.localeCompare(b.locationName, 'es', {
            sensitivity: 'base',
          })
        )

      const businessUnits = [...businessUnitsRaw]
        .map((bu) => ({
          ...bu,
          buName: formatSentence(bu.buName),
          buArea: formatSentence(bu.buArea),
          location: { ...bu.location, name: formatSentence(bu.location?.name) },
        }))
        .sort((a, b) =>
          (a.location?.name || a.buName || a.buArea).localeCompare(
            b.location?.name || b.buName || b.buArea,
            'es',
            { sensitivity: 'base' }
          )
        )

      // Bloque TOLERANTE: categorías + países. Nada de acá bloquea la pantalla;
      // lo que falle se degrada (categorías incompletas con aviso, países al
      // fallback de solo Venezuela).
      const [catType1, catType2, countryResult] = await Promise.allSettled([
        getPieceCategoriesDjango(1),
        getPieceCategoriesDjango(2),
        getCountriesPhoneCodes(),
      ])
      const catResults = [catType1, catType2]
      const countryPhoneCodes =
        countryResult.status === 'fulfilled' ? countryResult.value : []
      const fulfilled = catResults
        .filter(
          (r): r is PromiseFulfilledResult<PieceCategoryInterface[]> =>
            r.status === 'fulfilled'
        )
        .map((r) => r.value)
      const anyCategoryFailed = catResults.some((r) => r.status === 'rejected')
      const pieceCategories = mergeBulkCategories(fulfilled)
        .map((c) => ({ ...c, name: formatSentence(c.name) }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
        )

      if (anyCategoryFailed) {
        alertService.warn(
          'Categorias incompletas',
          'No se pudieron cargar todas las categorias (nacional/internacional). Algunas podrian faltar en la plantilla.'
        )
      }

      dispatch(
        bulkCatalogsLoaded({
          businessUnits,
          cities,
          citiesByBu,
          pieceCategories,
          countryPhoneCodes,
          // Sin países no hay nada que elegir: se trata igual que un fallo, que
          // es lo que el operador necesita saber (el campo solo ofrece +58).
          countryPhoneCodesError: countryPhoneCodes.length === 0,
        })
      )
    } catch (error) {
      console.error(
        'hydrateBulkCatalogs: fallo cargando tiendas/ciudades/relacion ciudad-tienda',
        error
      )
      dispatch(bulkCatalogsError())
      alertService.error(
        'Error al cargar catalogos',
        'No se pudieron cargar las tiendas o ciudades desde el servidor. Vuelve a intentarlo.',
        { autoClose: false }
      )
    }
  }
